/**
 * Spec 010 US1 (T036) — Cloud GPU connect modal.
 *
 * Dialog prompting the designer for the server's URL + bearer
 * token. On submit: validates URL is `https://`, calls
 * `createRemoteEngineAdapter(url, token)` which probes
 * `/v1/health` then `/v1/info` and constructs a
 * `RemoteEngineAdapter`. On success: invokes `onSuccess(adapter)`
 * (the parent App swaps in the new adapter); on failure:
 * surfaces the error inline (typically a token-rejection or a
 * network/cert problem) without dismissing the modal so the
 * user can retry with adjusted values.
 *
 * The modal seeds its fields from the user's last-used
 * `slot.cloud.*` localStorage entry (if any) so reconnecting
 * after a page reload is one click + Enter.
 */

import { useEffect, useState } from "react";
import { createRemoteEngineAdapter } from "../engine/createEngineAdapter";
import { getRemoteConfig } from "../engine/remoteConfig";
import type { RemoteEngineAdapter } from "../engine/remote";

interface CloudGpuModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (adapter: RemoteEngineAdapter) => void;
}

export function CloudGpuModal({
  open,
  onClose,
  onSuccess,
}: CloudGpuModalProps) {
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seeded = getRemoteConfig();
    // Reset every field on open. Without this, reopening after
    // `clearRemoteConfig()` (i.e. designer switched back to local
    // then back to cloud) would keep stale React state — including
    // a previously-typed bearer token (CR catch).
    if (seeded !== undefined) {
      setUrl(seeded.url);
      setToken(seeded.token);
    } else {
      setUrl("");
      setToken("");
    }
    setError(null);
    setSubmitting(false);
  }, [open]);

  // Escape-key dismissal — backdrop click + Cancel button cover the
  // pointer paths; Escape covers the keyboard path (CR catch / a11y).
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!/^https:\/\//i.test(url)) {
      setError("URL must start with https:// — TLS is required by the server.");
      return;
    }
    if (token === "") {
      setError("Bearer token is required.");
      return;
    }
    setSubmitting(true);
    try {
      const adapter = await createRemoteEngineAdapter(url, token);
      onSuccess(adapter);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cloud-gpu-modal-title"
      data-testid="cloud-gpu-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-paper-bright/20 p-4 dark:bg-term-bg/80"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-4 border border-paper-border bg-paper-bg p-5 dark:border-term-border dark:bg-term-bg"
      >
        <h2
          id="cloud-gpu-modal-title"
          className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright"
        >
          Connect Cloud GPU
        </h2>
        <p className="text-xs text-paper-text dark:text-term-text">
          Paste the URL and bearer token from your operator. Both are stored
          locally under <code>slot.cloud.*</code> in this browser only.
        </p>

        <div>
          <label
            htmlFor="cloud-url-input"
            className="block text-[10px] font-semibold uppercase tracking-widest text-paper-label dark:text-term-label"
          >
            Server URL
          </label>
          <input
            id="cloud-url-input"
            type="url"
            required
            placeholder="https://gpu.mad9000.com:8443"
            value={url}
            onChange={(e) => setUrl(e.target.value.trim())}
            className="mt-1 w-full border border-paper-border bg-paper-panel px-2 py-1.5 font-mono text-sm text-paper-bright focus:border-sky-500 focus:outline-none dark:border-term-border dark:bg-term-panel dark:text-term-bright"
            autoFocus
            disabled={submitting}
            data-testid="cloud-url-input"
          />
        </div>

        <div>
          <label
            htmlFor="cloud-token-input"
            className="block text-[10px] font-semibold uppercase tracking-widest text-paper-label dark:text-term-label"
          >
            Bearer token
          </label>
          <input
            id="cloud-token-input"
            type="password"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="mt-1 w-full border border-paper-border bg-paper-panel px-2 py-1.5 font-mono text-sm text-paper-bright focus:border-sky-500 focus:outline-none dark:border-term-border dark:bg-term-panel dark:text-term-bright"
            disabled={submitting}
            data-testid="cloud-token-input"
          />
        </div>

        {error !== null && (
          <div
            role="alert"
            className="border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800 dark:border-rose-700/50 dark:bg-rose-950/40 dark:text-rose-200"
            data-testid="cloud-gpu-modal-error"
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="border border-paper-border px-3 py-1.5 text-sm text-paper-text hover:bg-paper-panel disabled:opacity-50 dark:border-term-border dark:text-term-text dark:hover:bg-term-panel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-sky-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-600 disabled:opacity-50 dark:bg-sky-600 dark:hover:bg-sky-500"
            data-testid="cloud-gpu-modal-submit"
          >
            {submitting ? "Connecting…" : "Connect"}
          </button>
        </div>
      </form>
    </div>
  );
}
